// Marketing — MerchantAccount schemas.
//
// MerchantAccount = the per-organization record that captures the Marketing
// tier provisioning state for a CharityStack org. Storage lives on the
// existing `${stage}OrganizationsTable` (the V2 org table); this initiative
// extends it with three columns: `pocketflow_business_id`,
// `pocketflow_last_synced_at`, and `marketing_enabled`.
//
// Source of truth: data-model.md §3.2 (organizations extensions).
// Spec: POCKETFLOW_SPEC.md §4.6.1 (Provisioning workflow — v1 covers step 1
// only: POST /businesses; later milestones cover sender email, custom domain,
// initial contact sync, embed URL).

import { z } from 'zod';

import { organizationIdSchema } from './broadcasts.js';
import { isValidUsAreaCode, MAX_PREFERRED_PREFIXES } from './us-area-codes.js';

// PocketFlows business ID — opaque string returned by PF on business
// creation. Stored as `pocketflow_business_id` on the organizations table.
export const pocketflowBusinessIdSchema = z
  .string()
  .min(1, { message: 'pocketflowBusinessId must be non-empty' })
  .max(256, { message: 'pocketflowBusinessId too long' });
export type PocketflowBusinessId = z.infer<typeof pocketflowBusinessIdSchema>;

// Provisioning status enum.
//   - NOT_ENABLED:      Marketing tier never activated for this org.
//   - PROVISIONING:     enable() is in flight (PF business being created).
//                       Used by the optimistic UI between request and result.
//   - ACTIVE:           pocketflow_business_id present, tier enabled.
//   - DISABLED:         tier previously active, now deactivated by the org
//                       (explicit pause). The pocketflow_business_id is retained
//                       so re-enable can reuse the existing PF Business instead
//                       of creating a duplicate.
//   - READ_ONLY:        the org's marketing entitlement ENDED (cancelled /
//                       downgraded to STARTER). Distinct from DISABLED:
//                       DISABLED is a reversible self-pause that keeps the PF
//                       business; READ_ONLY is the post-cancellation terminal
//                       state where the PF business is TORN DOWN
//                       (`pocketflow_business_deleted_at` stamped, PE-1594).
//                       Reads (history) are still served from OUR mirror +
//                       OpenSearch; writes are rejected (assertMarketingWritable).
//                       Re-enable from READ_ONLY does NOT reuse the dead id — it
//                       takes the first-enable path and POSTs a fresh PF business
//                       (D16: gated on pocketflow_business_deleted_at).
export const marketingTierStatusSchema = z.enum([
  'NOT_ENABLED',
  'PROVISIONING',
  'ACTIVE',
  'DISABLED',
  'READ_ONLY',
]);
export type MarketingTierStatus = z.infer<typeof marketingTierStatusSchema>;

// Sender email — mirrors PF's `email_address` object. PF auto-provisions
// the first one when a business is created, on a PF-owned shared domain
// (e.g. `bu_xxx-yyy@dev.pocketflows.com` in sandbox). The user can edit
// display_name, reply_to, and username via PF API; the domain is fixed
// unless a custom_domain is configured (out of scope for v1).
export const senderEmailSchema = z.object({
  id: z.string().min(1),
  address: z.string().min(1),
  displayName: z.string(),
  // Nullable in PF's response shape — "Not set" → null.
  replyTo: z.string().nullable(),
});
export type SenderEmail = z.infer<typeof senderEmailSchema>;

// Phone number — mirrors PF's `phone_number` object. Read-only from our side:
// PF/Telgorithm manages provisioning; the PF API exposes no create/update/
// delete endpoints.
export const phoneNumberSchema = z.object({
  id: z.string().min(1),
  phoneNumber: z.string().min(1),
});
export type PhoneNumber = z.infer<typeof phoneNumberSchema>;

// The CharityStack SHARED "project" sending number(s) (a.k.a. the GLOBAL
// number). PocketFlows auto-attaches a shared number to every newly-onboarded
// live project at the business level — it is NOT the product of a
// dedicated-number business registration (PE-1819). Consequences the FE and BE
// both depend on:
//   - An org whose ONLY `phoneNumbers` entry is a shared number has NOT
//     completed business registration, so the dashboard must still offer the
//     request flow (not the "managed for you" terminal copy).
//   - It is labelled "Global" in the UI; a real registered number is "Private".
// Today PF uses ONE shared number in both dev and prod. It's modelled as a
// list so a future rollover / second project number is a one-line addition
// here rather than a predicate rewrite.
export const CHARITYSTACK_SHARED_SMS_NUMBER = '+15123252666';
export const SHARED_PROJECT_SMS_NUMBERS = [CHARITYSTACK_SHARED_SMS_NUMBER];

// Precomputed digit-only forms of the shared numbers (built once at module
// load). Comparing on digits means a formatting difference (`+1 (512) 325-2666`
// vs the E.164 `+15123252666`) can never produce a false negative.
const SHARED_PROJECT_SMS_NUMBER_DIGITS = new Set(
  SHARED_PROJECT_SMS_NUMBERS.map((n) => n.replace(/\D/g, '')),
);

// True when `phoneNumber` IS one of the shared project/global numbers above.
export function isSharedProjectSmsNumber(
  phoneNumber: string | null | undefined,
): boolean {
  if (!phoneNumber) return false;
  return SHARED_PROJECT_SMS_NUMBER_DIGITS.has(phoneNumber.replace(/\D/g, ''));
}

// DNS record returned by PF on custom_domain create. Each entry tells the
// user what to add to their DNS provider (Cloudflare, GoDaddy, etc.).
// All three fields are nullable per PF docs — we surface them verbatim.
export const dnsRecordSchema = z.object({
  type: z.string().nullable(),
  name: z.string().nullable(),
  value: z.string().nullable(),
});
export type DnsRecord = z.infer<typeof dnsRecordSchema>;

// Custom domain — mirrors PF's `custom_domain` object. After creation the
// domain is unvalidated until the user adds the DNS records on their side
// and we trigger POST /custom_domains/{id}/validate (or PF's automatic
// background validator catches it).
export const customDomainSchema = z.object({
  id: z.string().min(1),
  domain: z.string().min(1),
  validated: z.boolean(),
  dnsRecords: z.array(dnsRecordSchema),
});
export type CustomDomain = z.infer<typeof customDomainSchema>;

// One SMS-number request in the org's history (PE-1766). The FE read model —
// internal bookkeeping fields on the org row (requestedBy, the
// knownPhoneNumberIds detection snapshot) are deliberately NOT exposed here.
//   PENDING  — registration + provisioning-initialize submitted to PF;
//              awaiting their ~72-business-hour decision.
//   ACTIVE   — a new number appeared in GET /phone_numbers and was attributed
//              to this request (delta vs the request-time snapshot).
//   REJECTED — PF reported a rejection (manual, via Slack → internal ops
//              route); rejectionReason carries their verbatim reason.
export const smsNumberRequestStatusSchema = z.enum(['PENDING', 'ACTIVE', 'REJECTED']);
export type SmsNumberRequestStatus = z.infer<typeof smsNumberRequestStatusSchema>;

export const smsNumberRequestSchema = z.object({
  requestId: z.string().min(1),
  // Preferred 3-digit area codes submitted with this request. May be empty
  // (PF then picks the area code). Best-effort on PF's side — the assigned
  // number may be in a different area code.
  prefixes: z.array(z.string().regex(/^[0-9]{3}$/)),
  requestedAt: z.string().datetime({ offset: true }),
  status: smsNumberRequestStatusSchema,
  // ACTIVE only — the PF phone-number id + E.164 value attributed to this
  // request.
  assignedPhoneNumberId: z.string().min(1).optional(),
  assignedPhoneNumber: z.string().min(1).optional(),
  // ACTIVE or REJECTED — when the request left PENDING.
  resolvedAt: z.string().datetime({ offset: true }).optional(),
  // REJECTED only — PF's reason, verbatim (relayed from their Slack report).
  rejectionReason: z.string().optional(),
});
export type SmsNumberRequest = z.infer<typeof smsNumberRequestSchema>;

// The read model returned by getStatus and enableMarketing.
export const merchantAccountStatusSchema = z.object({
  organizationId: organizationIdSchema,
  status: marketingTierStatusSchema,
  // Present when status is ACTIVE or DISABLED (DISABLED retains the ID for
  // future re-enable without creating a duplicate PF Business).
  pocketflowBusinessId: pocketflowBusinessIdSchema.nullable(),
  // ISO 8601 with offset. Updated on every successful sync write to PF
  // (currently: business create only; later milestones will widen this).
  pocketflowLastSyncedAt: z.string().datetime({ offset: true }).nullable(),
  // Aggregate count of contacts whose last sync attempt failed. Drives
  // the FE warning panel in Marketing Settings ("X contacts didn't
  // get synced"). Null when no sync has ever run (i.e. tier not
  // activated yet or first activation still in flight). Zero means
  // "last run was fully successful." Written at the end of each seed
  // run; will be written by the live drain in Phase 2 as well.
  pocketflowFailedContactsCount: z.number().int().nonnegative().nullable(),
  // Per-category breakdown of the count above (PE-1684): contacts whose last
  // sync was permanently rejected on their email address vs phone number
  // (`other` is the defensive fallback for a rejection naming no recognizable
  // field). Each category equals the rows the dashboard's matching
  // `?syncError=<type>` filter shows, so the settings banner can display the
  // number next to its landing link. OPTIONAL — additive and
  // backwards-compatible (pre-existing getStatus consumers must not break);
  // null until the org's next seed/drain run writes the breakdown.
  pocketflowFailedContactsCounts: z
    .object({
      email: z.number().int().nonnegative(),
      phone: z.number().int().nonnegative(),
      other: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  // Live PF data — fetched alongside the org row when status === ACTIVE.
  // Empty arrays when the tier is not active (we don't call PF in that case).
  // Designed as arrays because the API returns lists; in practice every PF
  // business has exactly one auto-provisioned sender and zero or one
  // Telgorithm-provisioned phone number.
  senderEmails: z.array(senderEmailSchema),
  phoneNumbers: z.array(phoneNumberSchema),
  customDomains: z.array(customDomainSchema),
  // When the org asked for an SMS sending number (Marketing settings →
  // "Request SMS number"). PF has NO self-serve provisioning API —
  // numbers are assigned by the PocketFlows team via Telgorithm after
  // we relay the request — so this drives the "Requested / pending"
  // state until `phoneNumbers` becomes non-empty. Null = never asked.
  smsNumberRequestedAt: z.string().datetime({ offset: true }).nullable(),
  // PE-1766 — the org's SMS-number request history, newest first. OPTIONAL —
  // additive and backwards-compatible (pre-existing getStatus consumers must
  // not break); absent until the backend ships the request-tracking write
  // path. The legacy smsNumberRequestedAt above stays populated in parallel
  // during the transition.
  smsNumberRequests: z.array(smsNumberRequestSchema).optional(),
  // PE-1594 Banner A (cancelled-but-active). Sourced from the org's
  // billingDetails, written by PRICING on `customer.subscription.updated`
  // when the org cancels (Stripe sets `cancel_at_period_end`). Marketing's
  // getStatus surfaces them so the marketing pages (which already call
  // getStatus) can render the "Marketing ends <date> — full access until
  // then" heads-up with a Resume CTA, WITHOUT a second fetch (D13/D17).
  //   - cancelAtPeriodEnd: true once the org has cancelled but the paid
  //     window is still open. Reset to false/absent when they resume.
  //   - currentPeriodEnd: ISO-8601 end of the paid window (the date in the
  //     banner copy). Present alongside cancelAtPeriodEnd.
  // BOTH are OPTIONAL — additive and backwards-compatible: the marketing
  // backend ships its READ side (PR 2) BEFORE pricing writes these (PR 3),
  // and pre-existing getStatus consumers must not break. Absent ⇒ no Banner A.
  cancelAtPeriodEnd: z.boolean().optional(),
  currentPeriodEnd: z.string().datetime({ offset: true }).optional(),
});
export type MerchantAccountStatus = z.infer<typeof merchantAccountStatusSchema>;

// Update sender email input — all three fields are optional patches. PF's
// POST /email_addresses/{id} accepts a partial body; we forward only the
// fields the caller actually sent.
export const updateSenderEmailInputSchema = z
  .object({
    senderEmailId: z.string().min(1),
    displayName: z.string().min(1).max(256).optional(),
    // replyTo nullable because the user might want to CLEAR the reply-to.
    // We send `null` to PF when the caller explicitly nulls it, vs omitting
    // the field when they don't want to touch it.
    replyTo: z.string().email().nullable().optional(),
    // Username = local part of the address (before @). Domain stays whatever
    // PF auto-assigned — changing domain requires custom_domain (deferred).
    // Allowed chars: a-z A-Z 0-9 . _ - + (RFC 5321 permits more but these
    // are the characters every inbox provider and PF reliably accept).
    username: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-zA-Z0-9._+-]+$/, {
        message:
          'Username may only contain letters, digits, dot, underscore, hyphen, and plus. Do not include the @ sign or domain — the domain is fixed.',
      })
      .optional(),
  })
  .strict();
export type UpdateSenderEmailInput = z.infer<typeof updateSenderEmailInputSchema>;

export const updateSenderEmailOutputSchema = senderEmailSchema;
export type UpdateSenderEmailOutput = SenderEmail;

// Delete a sender email. Caller passes the PF email_address id; we
// validate cross-tenant ownership inside the use case before forwarding
// to PF DELETE.
export const deleteSenderEmailInputSchema = z
  .object({
    senderEmailId: z.string().min(1),
  })
  .strict();
export type DeleteSenderEmailInput = z.infer<typeof deleteSenderEmailInputSchema>;

export const deleteSenderEmailOutputSchema = z
  .object({
    deletedId: z.string().min(1),
  })
  .strict();
export type DeleteSenderEmailOutput = z.infer<typeof deleteSenderEmailOutputSchema>;

// Add custom domain — caller supplies the domain string they own (e.g.
// "mail.preservers.org"). PF returns the DNS records to display.
export const addCustomDomainInputSchema = z
  .object({
    // Conservative domain validation: lowercase letters, digits, dots,
    // hyphens. Length 4-253 per RFC 1035. We intentionally don't try to
    // validate TLDs — let PF reject what it can't handle.
    domain: z
      .string()
      .min(4)
      .max(253)
      .regex(/^(?=.{4,253}$)[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, {
        message:
          'Enter a valid lowercase domain like "mail.your-org.org" (no http://, no path, no @).',
      }),
  })
  .strict();
export type AddCustomDomainInput = z.infer<typeof addCustomDomainInputSchema>;

export const addCustomDomainOutputSchema = customDomainSchema;
export type AddCustomDomainOutput = CustomDomain;

// Validate custom domain — caller provides the PF custom_domain ID.
// Response includes the updated CustomDomain plus (on first-time success)
// the sender we auto-created on the validated domain. `createdSender` is
// null when the domain was already validated previously or sender creation
// was skipped/failed (the latter is logged server-side but doesn't fail
// the procedure — the user can edit/create senders manually).
export const validateCustomDomainInputSchema = z
  .object({
    customDomainId: z.string().min(1),
  })
  .strict();
export type ValidateCustomDomainInput = z.infer<typeof validateCustomDomainInputSchema>;

export const validateCustomDomainOutputSchema = z.object({
  customDomain: customDomainSchema,
  createdSender: senderEmailSchema.nullable(),
});
export type ValidateCustomDomainOutput = z.infer<typeof validateCustomDomainOutputSchema>;

// Create embed URL — POST to PF /businesses/{id}/embeds. The returned URL
// is what the dashboard loads in an iframe. Embed URLs have an expiry
// (server-determined, typically minutes) so the FE should request a fresh
// URL on every "new broadcast" / "new segment" navigation rather than
// caching. Per the spec, PF embeds don't currently support deep-linking
// to a specific screen — the URL opens PF's main dashboard and the user
// navigates within.
export const createEmbedInputSchema = z.object({}).strict();
export type CreateEmbedInput = z.infer<typeof createEmbedInputSchema>;

export const embedSchema = z.object({
  id: z.string().min(1),
  url: z.string().min(1),
  expiresAt: z.string().min(1),
});
export type Embed = z.infer<typeof embedSchema>;

export const createEmbedOutputSchema = embedSchema;
export type CreateEmbedOutput = Embed;

// Inputs are empty objects (the org is inferred from the auth context). We
// keep the shape as an object — not `z.void()` — so future fields can be
// added without breaking the wire contract.
export const enableMarketingInputSchema = z.object({}).strict();
export type EnableMarketingInput = z.infer<typeof enableMarketingInputSchema>;

// Disable input — empty body, same as enable. Distinct schema (instead
// of reusing enableMarketingInputSchema directly) so future fields can
// diverge without a breaking rename.
export const disableMarketingInputSchema = z.object({}).strict();
export type DisableMarketingInput = z.infer<typeof disableMarketingInputSchema>;

export const getMerchantAccountStatusInputSchema = z.object({}).strict();
export type GetMerchantAccountStatusInput = z.infer<
  typeof getMerchantAccountStatusInputSchema
>;

// Outputs mirror the read model. Re-exported aliases so callers can use the
// procedure-specific name and still get the same shape.
export const enableMarketingOutputSchema = merchantAccountStatusSchema;
export type EnableMarketingOutput = MerchantAccountStatus;

// Disable output — returns the updated MerchantAccountStatus with
// status=DISABLED. The pocketflow_business_id is preserved so a future
// re-enable reuses the same PF business (idempotent at PF API boundary).
export const disableMarketingOutputSchema = merchantAccountStatusSchema;
export type DisableMarketingOutput = MerchantAccountStatus;

export const getMerchantAccountStatusOutputSchema = merchantAccountStatusSchema;
export type GetMerchantAccountStatusOutput = MerchantAccountStatus;

// ─────────────────────────────────────────────────────────────────────────────
// Request SMS sending number
// ─────────────────────────────────────────────────────────────────────────────

// The marketing phone number — entered in Settings → Marketing → SMS and
// submitted with the request. Must be E.164 (e.g. +14697636720); the dashboard
// formats it via libphonenumber-js before sending. PF/TCR registers this as
// the business `contact.phone_number`. The use case persists it onto the org's
// registrationContact (marketing owns this write) and includes it in the PF
// registration packet.
// Preferred area codes (PE-1766): 0–5 distinct 3-digit codes, each validated
// against the shared US geographic area-code dataset — the same list the
// dashboard combobox offers, so FE and API can never disagree on what's real.
export const preferredPrefixesSchema = z
  .array(
    z
      .string()
      .regex(/^[0-9]{3}$/, { message: 'Each prefix must be exactly 3 digits' })
      .refine(isValidUsAreaCode, {
        message: 'Not a valid US area code',
      }),
  )
  .max(MAX_PREFERRED_PREFIXES, {
    message: `At most ${MAX_PREFERRED_PREFIXES} preferred area codes`,
  })
  .refine((arr) => new Set(arr).size === arr.length, {
    message: 'Duplicate area codes',
  });

// E.164 contact phone (e.g. +14697636720).
const e164PhoneSchema = z.string().regex(/^\+[1-9]\d{6,14}$/, {
  message: 'phoneNumber must be E.164 (e.g. +14697636720)',
});

export const requestSmsNumberInputSchema = z
  .object({
    // OPTIONAL since the contact phone moved to Settings → Organization
    // (saved via merchantAccount.saveMarketingPhone): omitted ⇒ the backend
    // uses the org's stored marketingPhone (refusing with the standard
    // missing-info error when neither exists). Still ACCEPTED for
    // backwards-compat — pre-move dashboard bundles send it inline, and it
    // then doubles as a save.
    phoneNumber: e164PhoneSchema.optional(),
    // OPTIONAL (defaults to []) so pre-PE-1766 dashboard bundles still in
    // browsers keep working against the strict input schema.
    preferredPrefixes: preferredPrefixesSchema.default([]),
  })
  .strict();
export type RequestSmsNumberInput = z.infer<typeof requestSmsNumberInputSchema>;

// Save the org's marketing/SMS contact phone (Settings → Organization,
// next to the Primary contact it belongs with). Marketing owns this
// attribute (`marketingPhone` on the org row) — accounts-backend's
// nonprofit-settings deliberately never writes it.
export const saveMarketingPhoneInputSchema = z
  .object({
    phoneNumber: e164PhoneSchema,
  })
  .strict();
export type SaveMarketingPhoneInput = z.infer<typeof saveMarketingPhoneInputSchema>;

export const saveMarketingPhoneOutputSchema = z
  .object({
    phoneNumber: e164PhoneSchema,
  })
  .strict();
export type SaveMarketingPhoneOutput = z.infer<typeof saveMarketingPhoneOutputSchema>;

export const requestSmsNumberOutputSchema = merchantAccountStatusSchema;
export type RequestSmsNumberOutput = z.infer<typeof requestSmsNumberOutputSchema>;
